import React, { useEffect, useRef, useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import { BPM_MIN, BPM_MAX } from "../../../lib/kitsBrowse";

function nextTolerance(current) {
  if (current === 5) return 10;
  if (current === 10) return 20;
  return 5;
}

// design doc section 5.3: a target tempo with a tolerance window, not a
// range slider and not fixed buckets (section 5.2's rejected alternatives).
//
// History (design section 10.2): typing AND the stepper buttons both commit
// via `onBpmChange` with history "replace" -- the parent (KitsCatalogue)
// applies {replace:true} to that callback. Tolerance and half/double are
// discrete choices and commit via `onToleranceChange`/`onHalfDoubleChange`,
// which the parent pushes instead.
export default function BpmControl({ bpm, tolerance, halfDouble, onBpmChange, onToleranceChange, onHalfDoubleChange }) {
  const [draft, setDraft] = useState(bpm == null ? "" : String(bpm));
  const focusedRef = useRef(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Resync from the URL when it changes from outside (Back, a pasted
    // link, an empty-state action) -- but never while the field has focus.
    if (!focusedRef.current) setDraft(bpm == null ? "" : String(bpm));
  }, [bpm]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const clampAndCommit = (raw) => {
    if (raw === "") {
      onBpmChange(null);
      return;
    }
    const n = Number(raw);
    if (Number.isInteger(n) && n >= BPM_MIN && n <= BPM_MAX) {
      onBpmChange(n);
    } else {
      // Out of range: the field shows what was typed until blur, then snaps
      // back to the last valid value (or empty) -- design section 5.3.
      setDraft(bpm == null ? "" : String(bpm));
    }
  };

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 3);
    setDraft(raw);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => clampAndCommit(raw), 250);
  };

  const step = (delta) => {
    clearTimeout(debounceRef.current);
    // "+ or - on an empty field sets 140. After that they step by 5, clamped
    // to 40..300" -- design section 5.3.
    const base = bpm == null ? 140 - delta : bpm;
    const next = bpm == null ? 140 : Math.min(BPM_MAX, Math.max(BPM_MIN, base + delta));
    setDraft(String(next));
    onBpmChange(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      clearTimeout(debounceRef.current);
      clampAndCommit(draft);
    } else if (e.key === "Escape") {
      clearTimeout(debounceRef.current);
      setDraft(bpm == null ? "" : String(bpm));
      inputRef.current?.blur();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      step(5);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      step(-5);
    }
  };

  const active = bpm != null;

  return (
    <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
      <span className="w-14 shrink-0 font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">
        BPM
      </span>
      {/* At 375px the stepper + tolerance + half/double + clear run past
          the 327px content column at the design-spec gaps (gap-3, px-2.5)
          -- trimmed ONLY below `sm`, same fix and reasoning as
          SortSegments. min-w-0 + overflow-x-auto stays as a safety net
          regardless, with the stepper (the primary control) first, so
          it's never what scrolls out of view. */}
      <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div
          className={`inline-flex h-9 shrink-0 items-stretch overflow-hidden rounded border bg-[#1a1a1e] transition-colors focus-within:border-[#FF5500] ${
            active ? "border-[#FF5500]" : "border-white/10"
          }`}
        >
          <button
            type="button"
            aria-label="Lower BPM by 5"
            onClick={() => step(-5)}
            className="flex w-9 items-center justify-center text-[#A1A1AA] hover:bg-white/5 hover:text-white"
          >
            <Minus size={14} />
          </button>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={3}
            placeholder="Any"
            aria-label="Target BPM"
            value={draft}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => (focusedRef.current = true)}
            onBlur={() => {
              focusedRef.current = false;
              clearTimeout(debounceRef.current);
              clampAndCommit(draft);
            }}
            className="w-14 bg-transparent text-center font-mono text-sm text-white placeholder:text-[#A1A1AA] focus:outline-none"
          />
          <button
            type="button"
            aria-label="Raise BPM by 5"
            onClick={() => step(5)}
            className="flex w-9 items-center justify-center text-[#A1A1AA] hover:bg-white/5 hover:text-white"
          >
            <Plus size={14} />
          </button>
        </div>

        <button
          type="button"
          disabled={!active}
          onClick={() => onToleranceChange(nextTolerance(tolerance))}
          aria-label={`Tempo window: plus or minus ${tolerance} BPM. Press to widen.`}
          className="inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded border border-white/10 bg-[#1a1a1e] px-1.5 sm:px-2.5 font-mono text-xs text-[#A1A1AA] hover:border-white/25 hover:text-white disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5500] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]"
        >
          ±{tolerance}
        </button>

        <button
          type="button"
          disabled={!active}
          aria-pressed={halfDouble}
          title="Also show half and double time"
          aria-label="Also show half and double time"
          onClick={() => onHalfDoubleChange(!halfDouble)}
          className={`inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded border px-1.5 sm:px-2.5 font-mono text-xs transition-colors disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5500] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505] ${
            halfDouble
              ? "border-[#FF5500] bg-[#FF5500]/10 text-[#FF5500]"
              : "border-white/10 bg-[#1a1a1e] text-[#A1A1AA] hover:border-white/25 hover:text-white"
          }`}
        >
          ½× 2×
        </button>

        {active && (
          <button
            type="button"
            aria-label="Clear BPM"
            onClick={() => onBpmChange(null)}
            className="flex w-9 shrink-0 items-center justify-center text-[#A1A1AA] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5500] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
